/* Which `Start here` steps a reader has ticked off, kept in this browser.
 *
 * **`localStorage`, and every access is wrapped**, because it throws rather than returning null in a
 * private window and where site data is blocked — and a guide that crashes the shell because a
 * reader has cookies off is a worse failure than a guide that forgets their ticks. Absence reads as
 * "nothing done yet", which is the correct starting state anyway.
 *
 * **Deliberately not on the server.** A tick is a note to oneself about reading a guide, not a fact
 * about the tenant: storing it would put a per-user row behind an endpoint, an authorization
 * question and a migration, for something whose worst failure is a checklist starting empty on a
 * second machine.
 */

/** The reference's key, kept byte-for-byte so a reader's existing ticks survive this port. */
const STORAGE_KEY = 'lumiq_guide_done';

export type GuideProgress = Readonly<Record<string, boolean>>;

export function readProgress(): GuideProgress {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    /* Narrowed rather than cast. What comes back is whatever is in that key — an older shape, a
     * half-written value, something another tab wrote — and handing an array or a string to the
     * panel would render `undefined` ticks rather than none. */
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const done: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (value === true) done[key] = true;
    }
    return done;
  } catch {
    return {};
  }
}

export function writeProgress(progress: GuideProgress): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    /* Storage is full, blocked, or this is a private window. The panel keeps the value in React
       state either way, so the session behaves correctly and only the memory of it is lost. */
  }
}

/** Toggling one step, as a value rather than a mutation, so the caller can store and render one
 * object and the two cannot disagree. */
export function toggled(progress: GuideProgress, id: string): GuideProgress {
  if (progress[id] === true) {
    /* Rebuilt without the key rather than deleted from a copy. A stored `false` would be a third
       state the reader of this object has to think about — `readProgress` already drops anything
       that is not `true`, so an absent key is the only way "not done" is spelled. */
    return Object.fromEntries(Object.entries(progress).filter(([key]) => key !== id));
  }
  return { ...progress, [id]: true };
}

export function doneCount(progress: GuideProgress, ids: readonly string[]): number {
  return ids.filter((id) => progress[id] === true).length;
}
